
import { useState } from "react";
import { MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import ParkingLocationMap from "@/components/vehicle/ParkingLocationMap";

interface VehicleParkingProps {
  vehicleId: string;
  initialParkingDetails: {
    address: string;
    lat: number | null;
    lng: number | null;
  };
  onParkingUpdate: () => void;
}

const VehicleParkingSection = ({ vehicleId, initialParkingDetails, onParkingUpdate }: VehicleParkingProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [parkingDetails, setParkingDetails] = useState(initialParkingDetails);
  const { toast } = useToast();

  const handleUpdateParking = async () => {
    try {
      const { error } = await supabase
        .from("vehicles")
        .update({
          parking_address: parkingDetails.address,
          parking_lat: parkingDetails.lat,
          parking_lng: parkingDetails.lng
        })
        .eq("id", vehicleId);

      if (error) throw error;

      toast({
        title: "Parkeerplek bijgewerkt",
        description: "De parkeerlocatie is succesvol bijgewerkt.",
      });
      
      setIsEditing(false);
      onParkingUpdate();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Fout bij bijwerken",
        description: error.message,
      });
    }
  };

  const handleLocationChange = (location: { address: string; lat: number; lng: number }) => {
    setParkingDetails({
      address: location.address,
      lat: location.lat,
      lng: location.lng
    });
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm flex-grow">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-[#B1A06D]" />
          <h2 className="text-xl font-semibold text-gray-900">Parkeerplek</h2>
        </div>
        <Button
          variant="outline"
          onClick={() => setIsEditing(!isEditing)}
        >
          {isEditing ? "Annuleren" : "Bewerken"}
        </Button>
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Adres</label>
            <Input
              value={parkingDetails.address}
              onChange={(e) =>
                setParkingDetails({ ...parkingDetails, address: e.target.value })
              }
              onBlur={(e) => {
                if (e.target.value) {
                  setParkingDetails(prev => ({ ...prev, lat: null, lng: null }));
                }
              }}
              placeholder="Volledig adres"
            />
          </div>
          <ParkingLocationMap
            address={parkingDetails.address}
            lat={parkingDetails.lat}
            lng={parkingDetails.lng}
            onLocationChange={handleLocationChange}
          />
          <Button
            className="w-full"
            onClick={handleUpdateParking}
            disabled={!parkingDetails.lat || !parkingDetails.lng}
          >
            Opslaan
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {initialParkingDetails.address ? (
            <>
              <p className="text-gray-700">
                {initialParkingDetails.address}
              </p>
              <ParkingLocationMap
                address={initialParkingDetails.address}
                lat={initialParkingDetails.lat}
                lng={initialParkingDetails.lng}
              />
            </>
          ) : (
            <p className="text-gray-500 italic">
              Nog geen parkeerlocatie toegevoegd
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default VehicleParkingSection;
